import React, { useEffect, useState, useId } from 'react'
import { useNls } from '../../nls/nlsProvider'
import {
  getApprovalLinkInfo,
  prepareApprovalOtp,
  openApproval,
  decideApproval,
  commentApproval,
} from '../../services/opsApprovalsService'
import {
  sendApprovalsPhoneOtp,
  signOutApprovals,
  type ConfirmationResult,
} from '../../services/approvalsApp'
import {
  classifyApprovalError,
  isRefusalReason,
  type RefusalReason,
} from './classifyApprovalError'
import { Button } from '../Common/Button'
import { TextInput } from '../Common/TextInput'
import { Textarea } from '../Common/Textarea'
import { PhoneNumberInput } from '../Common/PhoneNumberInput'
import './outsideApproval.css'

export interface ApprovalLandingProps {
  token: string
}

type Step = 'LOADING' | 'REFUSAL' | 'PHONE_ENTRY' | 'OTP_ENTRY' | 'REVIEW' | 'DONE'

export function ApprovalLanding({ token }: ApprovalLandingProps): React.ReactElement {
  const { t, locale } = useNls()
  const codeInputId = useId()
  const commentInputId = useId()

  const [step, setStep] = useState<Step>('LOADING')
  const [refusalReason, setRefusalReason] = useState<RefusalReason>('NOT_FOUND')

  const [linkInfo, setLinkInfo] = useState<Awaited<ReturnType<typeof getApprovalLinkInfo>> | null>(null)
  const [snapshotData, setSnapshotData] = useState<Awaited<ReturnType<typeof openApproval>> | null>(null)

  const [phone, setPhone] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  const [comment, setComment] = useState<string>('')
  const [commentsCount, setCommentsCount] = useState<number>(0)
  const [busy, setBusy] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const [doneDecision, setDoneDecision] = useState<'APPROVE' | 'REJECT' | null>(null)
  const [doneTime, setDoneTime] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadInfo() {
      try {
        const info = await getApprovalLinkInfo({ token })
        if (cancelled) return

        if (info.status === 'REVOKED') {
          setRefusalReason('REVOKED')
          setStep('REFUSAL')
          return
        }
        if (info.status === 'EXPIRED' || info.expiresAt <= Date.now()) {
          setRefusalReason('EXPIRED')
          setStep('REFUSAL')
          return
        }
        if (info.approverStatus === 'CLOSED') {
          setRefusalReason('CLOSED')
          setStep('REFUSAL')
          return
        }
        if (
          info.status === 'APPROVED' ||
          info.status === 'REJECTED' ||
          info.approverStatus === 'APPROVED' ||
          info.approverStatus === 'REJECTED'
        ) {
          setRefusalReason('ALREADY_DECIDED')
          setStep('REFUSAL')
          return
        }

        setLinkInfo(info)
        setStep('PHONE_ENTRY')
      } catch (err: unknown) {
        if (cancelled) return
        const classified = classifyApprovalError(err)
        if (isRefusalReason(classified)) {
          setRefusalReason(classified)
        } else {
          setRefusalReason('GENERIC')
        }
        setStep('REFUSAL')
      }
    }

    void loadInfo()

    return () => {
      cancelled = true
      void signOutApprovals()
    }
  }, [token])

  const handleSendOtp = async () => {
    if (!linkInfo) return
    setError(null)

    const maskedTrailingMatch = linkInfo.phoneMasked.match(/(\d{4})\s*$/)
    const last4Expected = maskedTrailingMatch ? maskedTrailingMatch[1] : linkInfo.phoneMasked.replace(/\D/g, '').slice(-4)
    const enteredDigits = phone.replace(/\D/g, '')
    const last4Entered = enteredDigits.slice(-4)

    if (last4Entered !== last4Expected || enteredDigits.length < 7) {
      setError(t('NLSOpsApprovalPhoneMismatch'))
      return
    }

    setBusy(true)
    try {
      await prepareApprovalOtp({ token })
      const confirmation = await sendApprovalsPhoneOtp(phone.startsWith('+') ? phone : `+${phone}`)
      setConfirmationResult(confirmation)
      setStep('OTP_ENTRY')
    } catch (err: unknown) {
      const classified = classifyApprovalError(err)
      if (classified === 'CAP') {
        setError(t('NLSOpsApprovalCapReached'))
      } else if (classified === 'NETWORK') {
        setError(t('NLSOpsApprovalNetworkError'))
      } else if (isRefusalReason(classified) && classified !== 'GENERIC') {
        setRefusalReason(classified)
        setStep('REFUSAL')
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  const handleResendOtp = async () => {
    if (!linkInfo) return
    setError(null)
    setBusy(true)
    try {
      await prepareApprovalOtp({ token })
      const confirmation = await sendApprovalsPhoneOtp(phone.startsWith('+') ? phone : `+${phone}`)
      setConfirmationResult(confirmation)
    } catch (err: unknown) {
      const classified = classifyApprovalError(err)
      if (classified === 'CAP') {
        setError(t('NLSOpsApprovalCapReached'))
      } else if (classified === 'NETWORK') {
        setError(t('NLSOpsApprovalNetworkError'))
      } else if (isRefusalReason(classified) && classified !== 'GENERIC') {
        setRefusalReason(classified)
        setStep('REFUSAL')
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!confirmationResult) return
    setError(null)

    if (!code || code.trim().length !== 6) {
      setError(t('NLSOpsApprovalInvalidCode'))
      return
    }

    setBusy(true)
    try {
      await confirmationResult.confirm(code.trim())
      const snap = await openApproval({ token })
      setSnapshotData(snap)
      setStep('REVIEW')
    } catch (err: unknown) {
      const classified = classifyApprovalError(err)
      if (classified === 'PERMISSION_DENIED') {
        setError(t('NLSOpsApprovalPermissionDenied'))
      } else if (classified === 'INVALID_CODE') {
        setError(t('NLSOpsApprovalInvalidCode'))
      } else if (classified === 'NETWORK') {
        setError(t('NLSOpsApprovalNetworkError'))
      } else if (isRefusalReason(classified) && classified !== 'GENERIC') {
        setRefusalReason(classified)
        setStep('REFUSAL')
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  const handleDecision = async (decision: 'APPROVE' | 'REJECT') => {
    setError(null)
    if (decision === 'REJECT') {
      if (!comment || comment.trim().length < 3) {
        setError(t('NLSOpsApprovalReasonRequired'))
        return
      }
    }

    setBusy(true)
    try {
      const opId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `op-${Date.now()}`
      await decideApproval({
        token,
        decision,
        comment: comment.trim() || undefined,
        opId,
      })
      await signOutApprovals()
      setDoneDecision(decision)
      setDoneTime(new Date().toLocaleTimeString(locale ?? 'en-US', { hour: '2-digit', minute: '2-digit' }))
      setStep('DONE')
    } catch (err: unknown) {
      const classified = classifyApprovalError(err)
      if (isRefusalReason(classified) && classified !== 'GENERIC' && classified !== 'NETWORK') {
        setRefusalReason(classified)
        setStep('REFUSAL')
      } else if (classified === 'NETWORK') {
        setError(t('NLSOpsApprovalNetworkError'))
      } else if (classified === 'CAP') {
        setError(t('NLSOpsApprovalCapReached'))
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  const handleAddComment = async () => {
    if (!comment || comment.trim().length < 1) return
    if (commentsCount >= 5) return
    setError(null)

    setBusy(true)
    try {
      const opId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `op-${Date.now()}`
      const res = await commentApproval({
        token,
        comment: comment.trim(),
        opId,
      })
      setCommentsCount(res.commentsCount)
      setComment('')
    } catch (err: unknown) {
      const classified = classifyApprovalError(err)
      if (isRefusalReason(classified) && classified !== 'GENERIC' && classified !== 'NETWORK') {
        setRefusalReason(classified)
        setStep('REFUSAL')
      } else if (classified === 'NETWORK') {
        setError(t('NLSOpsApprovalNetworkError'))
      } else if (classified === 'CAP') {
        setError(t('NLSOpsApprovalCapReached'))
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  // Refusal View
  if (step === 'REFUSAL') {
    let refusalText = t('NLSOpsApprovalNotFound')
    if (refusalReason === 'REVOKED') refusalText = t('NLSOpsApprovalRevoked')
    if (refusalReason === 'EXPIRED') refusalText = t('NLSOpsApprovalExpired')
    if (refusalReason === 'CLOSED') refusalText = t('NLSOpsApprovalClosed')
    if (refusalReason === 'ALREADY_DECIDED') refusalText = t('NLSOpsApprovalAlreadyDecided')
    if (refusalReason === 'PERMISSION_DENIED') refusalText = t('NLSOpsApprovalPermissionDenied')
    if (refusalReason === 'NETWORK') refusalText = t('NLSOpsApprovalNetworkError')
    if (refusalReason === 'GENERIC') refusalText = t('NLSOpsApprovalGenericError')

    return (
      <main className="outsideApprovalContainer" role="main">
        <div className="outsideApprovalCard">
          <div className="outsideApprovalBanner outsideApprovalBanner--error" role="alert">
            {refusalText}
          </div>
          <div className="outsideApprovalDisclaimer">{t('NLSOpsApprovalRunsOnTimelining')}</div>
        </div>
      </main>
    )
  }

  // Done View
  if (step === 'DONE') {
    return (
      <main className="outsideApprovalContainer" role="main">
        <div className="outsideApprovalCard">
          <div className="outsideApprovalHeader">
            <div className="outsideApprovalAvatar">
              {(linkInfo?.workspaceName || 'W').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="outsideApprovalOrgName">{linkInfo?.workspaceName}</div>
              <div className="outsideApprovalOrgSub">{t('NLSOpsApprovalAsksForApproval')}</div>
            </div>
          </div>
          <div className="outsideApprovalBanner outsideApprovalBanner--success" role="status">
            <div>
              {doneDecision === 'APPROVE'
                ? t('NLSOpsApprovalDoneApprovedTitle', { time: doneTime || '' })
                : doneDecision === 'REJECT'
                  ? t('NLSOpsApprovalDoneRejectedTitle', { time: doneTime || '' })
                  : t('NLSOpsApprovalDoneDecidedTitle', { time: doneTime || '' })}
            </div>
            <div style={{ marginTop: 4 }}>
              {t('NLSOpsApprovalDoneMessage', {
                workspace: linkInfo?.workspaceName || 'The workspace',
              })}
            </div>
          </div>
          <div className="outsideApprovalDisclaimer">{t('NLSOpsApprovalRunsOnTimelining')}</div>
        </div>
      </main>
    )
  }

  return (
    <main className="outsideApprovalContainer" role="main">
      <div className="outsideApprovalCard">
        {/* Header */}
        <div className="outsideApprovalHeader">
          <div className="outsideApprovalAvatar">
            {(linkInfo?.workspaceName || 'W').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="outsideApprovalOrgName">{linkInfo?.workspaceName}</div>
            <div className="outsideApprovalOrgSub">{t('NLSOpsApprovalAsksForApproval')}</div>
          </div>
        </div>

        <h1 className="outsideApprovalTitle">{linkInfo?.title || 'Outside Approval'}</h1>

        {error && (
          <div className="outsideApprovalBanner outsideApprovalBanner--error" role="alert">
            {error}
          </div>
        )}

        {/* Step: Phone Entry */}
        {step === 'PHONE_ENTRY' && (
          <div>
            <p className="outsideApprovalMeta">
              <span>{t('NLSOpsApprovalPhonePrompt')}</span>
            </p>
            <div className="outsideApprovalSnapshotCard">
              <div className="outsideApprovalFieldRow">
                <span className="outsideApprovalFieldLabel">Phone on record</span>
                <span className="outsideApprovalFieldValue outsideApprovalMono">
                  {linkInfo?.phoneMasked}
                </span>
              </div>
            </div>
            <div style={{ margin: '16px 0' }}>
              <label className="outsideApprovalFieldLabel">
                <span>{t('NLSOpsApprovalApproverPhone')}</span>
                <PhoneNumberInput
                  value={phone}
                  onChange={setPhone}
                  disabled={busy}
                  localPlaceholder="Phone number"
                />
              </label>
            </div>
            <div className="outsideApprovalActions">
              <Button
                variant="primary"
                onClick={handleSendOtp}
                disabled={busy || !phone}
              >
                {t('NLSOpsApprovalSendCode')}
              </Button>
            </div>
          </div>
        )}

        {/* Step: OTP Entry */}
        {step === 'OTP_ENTRY' && (
          <div>
            <p className="outsideApprovalMeta">
              <span>
                {t('NLSOpsApprovalCodeSentTo', {
                  phone: linkInfo?.phoneMasked || phone,
                })}
              </span>
            </p>
            <div className="outsideApprovalOtpGroup">
              <label htmlFor={codeInputId} className="outsideApprovalFieldLabel">
                6-digit code
              </label>
              <TextInput
                id={codeInputId}
                type="text"
                value={code}
                onChange={setCode}
                placeholder="123456"
                disabled={busy}
              />
            </div>
            <div className="outsideApprovalActions">
              <Button
                variant="primary"
                onClick={handleVerifyOtp}
                disabled={busy || code.trim().length !== 6}
              >
                {t('NLSOpsApprovalVerifyCode')}
              </Button>
              <Button
                variant="secondary"
                onClick={handleResendOtp}
                disabled={busy}
              >
                {t('NLSOpsApprovalResendCode')}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Review Snapshot */}
        {step === 'REVIEW' && snapshotData && (
          <div>
            <div className="outsideApprovalMeta">
              <span>
                {t('NLSOpsApprovalVersion', {
                  version: String(snapshotData.approval.subjectVersion),
                })}
              </span>
              <span>·</span>
              <span className="outsideApprovalMono">
                #{snapshotData.approval.snapshotHash.slice(0, 4)}…
                {snapshotData.approval.snapshotHash.slice(-4)}
              </span>
              <span>·</span>
              <span>
                {t('NLSOpsApprovalExpiresOn', {
                  date: new Date(snapshotData.approval.expiresAt).toLocaleDateString(
                    locale ?? 'en-US',
                    { day: 'numeric', month: 'short' },
                  ),
                })}
              </span>
            </div>

            {/* Snapshot Fields */}
            <div className="outsideApprovalSnapshotCard">
              {Object.entries(snapshotData.approval.snapshot).map(([key, val]) => (
                <div key={key} className="outsideApprovalFieldRow">
                  <span className="outsideApprovalFieldLabel">{key}</span>
                  <span className="outsideApprovalFieldValue">
                    {typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val)}
                  </span>
                </div>
              ))}
            </div>

            {/* Comment / Reason Input */}
            <div style={{ marginTop: 16 }}>
              <label htmlFor={commentInputId} className="outsideApprovalFieldLabel">
                {t('NLSOpsApprovalCommentOptional')}
              </label>
              <Textarea
                id={commentInputId}
                value={comment}
                onChange={setComment}
                placeholder="Enter comment or reason..."
                disabled={busy}
                rows={3}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 6,
                }}
              >
                <span className="outsideApprovalMeta" style={{ margin: 0 }}>
                  {t('NLSOpsApprovalCommentCountLeft', {
                    count: String(Math.max(0, 5 - commentsCount)),
                  })}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={handleAddComment}
                  disabled={busy || !comment.trim() || commentsCount >= 5}
                >
                  {t('NLSOpsApprovalComment')}
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="outsideApprovalActions">
              <div className="outsideApprovalButtonRow">
                <Button
                  variant="primary"
                  onClick={() => handleDecision('APPROVE')}
                  disabled={busy}
                >
                  {t('NLSOpsApprovalApprove')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleDecision('REJECT')}
                  disabled={busy}
                >
                  {t('NLSOpsApprovalReject')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Disclaimer */}
        <div className="outsideApprovalDisclaimer">
          <div>{t('NLSOpsApprovalLegalDisclaimer')}</div>
          <div style={{ marginTop: 4 }}>{t('NLSOpsApprovalRunsOnTimelining')}</div>
        </div>
      </div>
    </main>
  )
}
